import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Input,
  Select,
  Text,
  Badge,
} from '@chakra-ui/react';

function nextAvailableId(items, prefix) {
  const used = new Set(
    items
      .map((it) => Number(String(it.id).replace(prefix, '')))
      .filter((n) => !Number.isNaN(n))
  );
  let n = 1;
  while (used.has(n)) n += 1;
  return `${prefix}${n}`;
}

export default function MatchingEditor({ value, onChange }) {
  const [leftLabel, setLeftLabel] = useState(value?.left_label || '');
  const [rightLabel, setRightLabel] = useState(value?.right_label || '');
  const [left, setLeft] = useState(value?.left || []);
  const [right, setRight] = useState(value?.right || []);
  const [pairs, setPairs] = useState(value?.pairs || []);

  useEffect(() => {
    setLeftLabel(value?.left_label || '');
    setRightLabel(value?.right_label || '');
    setLeft(value?.left || []);
    setRight(value?.right || []);
    setPairs(value?.pairs || []);
  }, [value?.left_label]);

  const emit = (next) => {
    onChange({
      version: 1,
      left_label: next.leftLabel ?? leftLabel,
      right_label: next.rightLabel ?? rightLabel,
      left: next.left ?? left,
      right: next.right ?? right,
      pairs: next.pairs ?? pairs,
    });
  };

  const handleLabelLeft = (v) => {
    setLeftLabel(v);
    emit({ leftLabel: v });
  };

  const handleLabelRight = (v) => {
    setRightLabel(v);
    emit({ rightLabel: v });
  };

  const handleAddLeft = () => {
    const id = nextAvailableId(left, 'a');
    const next = [...left, { id, label: '' }];
    setLeft(next);
    emit({ left: next });
  };

  const handleAddRight = () => {
    const id = nextAvailableId(right, 'b');
    const next = [...right, { id, label: '' }];
    setRight(next);
    emit({ right: next });
  };

  const handleEditLeft = (id, label) => {
    const next = left.map((it) => (it.id === id ? { ...it, label } : it));
    setLeft(next);
    emit({ left: next });
  };

  const handleEditRight = (id, label) => {
    const next = right.map((it) => (it.id === id ? { ...it, label } : it));
    setRight(next);
    emit({ right: next });
  };

  const handleRemoveLeft = (id) => {
    const nextLeft = left.filter((it) => it.id !== id);
    const nextPairs = pairs.filter((p) => p.leftId !== id);
    setLeft(nextLeft);
    setPairs(nextPairs);
    emit({ left: nextLeft, pairs: nextPairs });
  };

  const handleRemoveRight = (id) => {
    const nextRight = right.filter((it) => it.id !== id);
    const nextPairs = pairs.filter((p) => p.rightId !== id);
    setRight(nextRight);
    setPairs(nextPairs);
    emit({ right: nextRight, pairs: nextPairs });
  };

  const handlePair = (leftId, rightId) => {
    const filtered = pairs.filter((p) => p.leftId !== leftId);
    const next = rightId ? [...filtered, { leftId, rightId }] : filtered;
    setPairs(next);
    emit({ pairs: next });
  };

  const currentRightFor = (leftId) => {
    const p = pairs.find((x) => x.leftId === leftId);
    return p ? p.rightId : '';
  };

  return (
    <Box>
      <Text fontSize="sm" fontWeight="500" mb={1}>
        Nombres de las columnas
      </Text>
      <Text fontSize="xs" color="gray.500" mb={2}>
        Ej.: Autor / Obra, Causa / Efecto, Evento / Año.
      </Text>
      <Grid templateColumns="1fr auto 1fr" gap={2} alignItems="center" mb={4}>
        <Input
          size="sm"
          placeholder="Columna izquierda"
          value={leftLabel}
          onChange={(e) => handleLabelLeft(e.target.value)}
          borderStyle="dashed"
          borderColor="#afa9ec"
          color="#3c3489"
          fontWeight="500"
        />
        <Text color="gray.400" aria-hidden>
          ↔
        </Text>
        <Input
          size="sm"
          placeholder="Columna derecha"
          value={rightLabel}
          onChange={(e) => handleLabelRight(e.target.value)}
          borderStyle="dashed"
          borderColor="#9fe1cb"
          color="#0f6e56"
          fontWeight="500"
        />
      </Grid>

      <Grid templateColumns="1fr 1fr" gap={4} mb={4}>
        <GridItem>
          <Flex alignItems="center" gap={2} mb={2}>
            <Badge bg="#eeedfe" color="#3c3489" px={2} py={1} borderRadius="md">
              A
            </Badge>
            <Text fontSize="sm" fontWeight="500" color="#3c3489">
              Items de "{leftLabel || 'columna izquierda'}"
            </Text>
          </Flex>
          {left.map((it, idx) => (
            <Flex key={it.id} gap={2} mb={2} alignItems="center">
              <Badge bg="#eeedfe" color="#3c3489" px={2} py={1} borderRadius="md" minW="28px" textAlign="center">
                {idx + 1}
              </Badge>
              <Input
                size="sm"
                placeholder="Texto del ítem"
                value={it.label}
                onChange={(e) => handleEditLeft(it.id, e.target.value)}
              />
              <IconButton
                aria-label="Eliminar"
                size="sm"
                variant="ghost"
                colorScheme="red"
                icon={<span aria-hidden>×</span>}
                onClick={() => handleRemoveLeft(it.id)}
              />
            </Flex>
          ))}
          <Button
            size="sm"
            variant="outline"
            borderStyle="dashed"
            borderColor="#574feb"
            color="#574feb"
            width="100%"
            onClick={handleAddLeft}
          >
            ＋ Añadir
          </Button>
        </GridItem>

        <GridItem>
          <Flex alignItems="center" gap={2} mb={2}>
            <Badge bg="#e1f5ee" color="#0f6e56" px={2} py={1} borderRadius="md">
              B
            </Badge>
            <Text fontSize="sm" fontWeight="500" color="#0f6e56">
              Items de "{rightLabel || 'columna derecha'}"
            </Text>
          </Flex>
          {right.map((it, idx) => (
            <Flex key={it.id} gap={2} mb={2} alignItems="center">
              <Badge bg="#e1f5ee" color="#0f6e56" px={2} py={1} borderRadius="md" minW="28px" textAlign="center">
                {idx + 1}
              </Badge>
              <Input
                size="sm"
                placeholder="Texto del ítem"
                value={it.label}
                onChange={(e) => handleEditRight(it.id, e.target.value)}
              />
              <IconButton
                aria-label="Eliminar"
                size="sm"
                variant="ghost"
                colorScheme="red"
                icon={<span aria-hidden>×</span>}
                onClick={() => handleRemoveRight(it.id)}
              />
            </Flex>
          ))}
          <Button
            size="sm"
            variant="outline"
            borderStyle="dashed"
            borderColor="#0f6e56"
            color="#0f6e56"
            width="100%"
            onClick={handleAddRight}
          >
            ＋ Añadir
          </Button>
        </GridItem>
      </Grid>

      <Box>
        <Text fontSize="sm" fontWeight="500" mb={2}>
          Pares correctos
        </Text>
        {left.length === 0 || right.length === 0 ? (
          <Text fontSize="xs" color="gray.500">
            Agrega al menos un ítem en cada columna para definir los pares.
          </Text>
        ) : (
          <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" px={3} py={2}>
            {left.map((it, idx) => (
              <Grid
                key={it.id}
                templateColumns="1fr auto 1fr"
                alignItems="center"
                gap={2}
                py={2}
                borderTop={idx === 0 ? 'none' : '1px dashed'}
                borderColor="gray.200"
              >
                <Flex bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" px={2} py={1} alignItems="center" gap={2}>
                  <Badge bg="#eeedfe" color="#3c3489" px={2} py={0.5} borderRadius="md">
                    {idx + 1}
                  </Badge>
                  <Text fontSize="sm" noOfLines={1}>
                    {it.label || <em style={{ color: '#9ca3af' }}>sin texto</em>}
                  </Text>
                </Flex>
                <Text color="gray.500" aria-hidden>
                  ↔
                </Text>
                <Select
                  size="sm"
                  placeholder="Sin emparejar"
                  value={currentRightFor(it.id)}
                  onChange={(e) => handlePair(it.id, e.target.value)}
                >
                  {right.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label || '(sin texto)'}
                    </option>
                  ))}
                </Select>
              </Grid>
            ))}
          </Box>
        )}
        <Text fontSize="xs" color="gray.500" mt={2}>
          Cada elemento de A debe emparejarse con uno de B. El estudiante verá las columnas barajadas.
        </Text>
      </Box>
    </Box>
  );
}
